import { Calendar, Clock, Banknote, Shield, BarChart3, Briefcase, MapPin, Star, Users, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { PrefetchLink } from "@/components/ui/prefetch-link";
import { PERSONA_ACCENT_CLASSES, type PersonaAccentKey } from "@/lib/themeConstants";

const employerFeatures = [
  { icon: Calendar, title: "Shift Management", description: "Create and manage shifts effortlessly", href: "/employers/features/shift-management/" },
  { icon: Users, title: "Staff Management", description: "Manage your temp workforce", href: "/employers/features/staff-management/" },
  { icon: BarChart3, title: "Budget & Reporting", description: "Track spending and generate reports", href: "/employers/features/budget-reporting/" },
  { icon: Shield, title: "Advanced Features", description: "Compliance and verification tools", href: "/employers/features/advanced/" },
];

const agencyFeatures = [
  { icon: Users, title: "Worker Management", description: "Manage your worker database", href: "/agencies/features/worker-management/" },
  { icon: Building2, title: "Employer Portal", description: "Centralized employer management", href: "/agencies/features/employer-portal/" },
  { icon: Briefcase, title: "Shift Matching", description: "AI-powered shift matching", href: "/agencies/features/shift-matching/" },
  { icon: Clock, title: "Operations", description: "Streamline daily operations", href: "/agencies/features/operations/" },
];

const tempFeatures = [
  { icon: MapPin, title: "Shift Discovery", description: "Find shifts near you", href: "/temps/features/shift-discovery/" },
  { icon: Calendar, title: "Schedule", description: "Manage your availability", href: "/temps/features/schedule/" },
  { icon: Banknote, title: "Earnings", description: "Track your earnings", href: "/temps/features/earnings/" },
  { icon: Star, title: "Career", description: "Build your reputation", href: "/temps/features/career/" },
];

export const featuresByRole: Record<string, typeof employerFeatures> = {
  Employers: employerFeatures,
  Agencies: agencyFeatures,
  Temps: tempFeatures,
};

interface DesktopMegaMenuProps {
  roleLabel: string;
  roleHref: string;
  roleName: string;
  accentKey?: PersonaAccentKey;
}

export default function DesktopMegaMenu({ roleLabel, roleHref, roleName, accentKey = "primary" }: DesktopMegaMenuProps) {
  const features = featuresByRole[roleLabel];
  if (!features) return null;

  const accent = PERSONA_ACCENT_CLASSES[accentKey];
  return (
    <div className="absolute left-0 top-full pt-4 animate-in fade-in-0 zoom-in-95 duration-200">
      <div className="bg-card rounded-xl shadow-xl border border-border p-6 w-[500px]">
        <div className="grid grid-cols-2 gap-4">
          {features.map((feature) => (
            <PrefetchLink
              key={feature.href}
              to={feature.href}
              className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted group"
            >
              <div className={cn("p-2 rounded-lg", accent.iconBg, accent.iconText, accent.bgHover, accent.textHover)}>
                <feature.icon className="h-5 w-5" />
              </div>
              <div>
                <h4 className="font-medium text-sm text-foreground">{feature.title}</h4>
                <p className="text-xs text-muted-foreground mt-0.5">{feature.description}</p>
              </div>
            </PrefetchLink>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-border">
          <PrefetchLink
            to={`${roleHref.replace(/\/$/, "")}/features/`}
            className={cn("text-sm hover:underline font-medium", accent.textAccent)}
          >
            View all {roleName} features →
          </PrefetchLink>
        </div>
      </div>
    </div>
  );
}
